import React, { useEffect, useState, useMemo, useRef } from "react";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import {
  Box,
  Grid,
  Button,
  ButtonGroup,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { useNavigate, useLocation } from "react-router-dom";

import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../../services/api_service";
import dayjs from "dayjs";
import {
  GetSalesReportAPI,
  GetSalesPerformanceReportAPI,
  GetSalesExecutivesDropdownAPI,
  GetSalesPersonProductBreakdownAPI,
  GetProductSalesPersonBreakdownAPI,
} from "../../../config";
import Layout from "../../../layout/Layout";
import { AlertMessage } from "../../../components/AlertMessage";
import { formatNumber, formatAED } from "../../../utils/format";
import {
  reportViewToggleButtonSx,
  reportViewToggleGroupSx,
  reportViewSubToggleButtonSx,
  reportViewSubToggleGroupSx,
} from "./reportViewToggleStyles";
import { buildExportFileName } from "../../../utils/exportFileName";
import { emptyListFilters } from "../../../components/ListFilterBar";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

const SalesReports = () => {
  // Opened from the dashboard's Product Category Sales chart: filtered to that
  // category and the dashboard's date window (cleared with "Clear filter").
  const dashNav = useLocation().state || {};
  const [dashCategory, setDashCategory] = useState(dashNav.category || "");
  const [tblList, setTblList] = useState([]);
  // "product" = product-wise sales (default), "revenue" = sales performance
  // (own row per sales person; "all" grouping mode below rolls every person's
  // full-range totals into one row each — this replaced the old standalone
  // "User-wise" tab, which was confusing sitting side by side with this one).
  // Persisted so drilling into a row and hitting Back doesn't reset the tab.
  const [viewMode, setViewMode] = useState(
    () => sessionStorage.getItem("salesReportViewMode") || "product"
  );

  useEffect(() => {
    sessionStorage.setItem("salesReportViewMode", viewMode);
  }, [viewMode]);

  // "all" = totals per sales person, no date breakdown (the default — this is
  // what "User-wise" used to show on its own tab). "day"/"month"/"year" break
  // the same numbers down by period, same as before.
  const [revenuePeriod, setRevenuePeriod] = useState("all");
  const [salesExecId, setSalesExecId] = useState(
    dashNav.salesPersonId && dashNav.salesPersonId !== "all" ? dashNav.salesPersonId : "all"
  );
  const [salesExecList, setSalesExecList] = useState([]);

  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const response = await apiData("get", GetSalesExecutivesDropdownAPI);
        if (response?.data) {
          setSalesExecList(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch sales executives", error);
      }
    };
    fetchExecutives();
  }, []);

  const [store, setStore] = useState(null);
  // Store / Customer / Salesperson / Payment mode / Payment status filters (product-wise view)
  const [reportBar, setReportBar] = useState({ ...emptyListFilters });
  const barParams = () => ({
    customer_id: Number(reportBar.customerId) > 0 ? reportBar.customerId : undefined,
    sales_person_id: Number(reportBar.salesPersonId) > 0 ? reportBar.salesPersonId : undefined,
    payment_method: reportBar.paymentMode || undefined,
    payment_status: reportBar.paymentStatus || undefined,
  });
  const barStoreId = Number(reportBar.storeId) > 0 ? reportBar.storeId : null;
  const [summary, setSummary] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const [message, setMessage] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const navFrom = dashNav.fromDate ? dayjs(dashNav.fromDate) : null;
  const navTo = dashNav.toDate ? dayjs(dashNav.toDate) : null;
  const [fromDate, setFromDate] = useState(navFrom);
  const [toDate, setToDate] = useState(navTo);
  const [selectedFromDate, setSelectedFromDate] = useState(navFrom);
  const [selectedToDate, setSelectedToDate] = useState(navTo);
  const [rowCount, setRowCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState([]);

  const childRef = useRef();

  const [successMessage, setSuccessMessage] = useState("");

  // Product breakdown dialog — "which products did this sales executive
  // sell" drill-down from the Total Quantity Sold column, same scope
  // (sales executive + date range) as the Total Invoices / Total
  // Complimentary hyperlinks already drill into the Invoice list with.
  const [productBreakdown, setProductBreakdown] = useState(null);

  // Reverse drill-down, from the Product-wise report: "which sales
  // executives sold this product" — from that report's Total Quantity Sold
  // column, same date-range scope as the From/To Date filters above it.
  const [salesPersonBreakdown, setSalesPersonBreakdown] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]);

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Mirror fetchData's filter-shaping exactly (same date fields, same
      // "profit" -> "total_profit" column-id remap) so the export always
      // matches what's actually applied to the table on screen — not just
      // whatever's sitting typed-but-unsearched in the date pickers.
      const formattedFromDate = selectedFromDate
        ? dayjs(selectedFromDate).format("YYYY-MM-DD")
        : null;
      const formattedToDate = selectedToDate
        ? dayjs(selectedToDate).format("YYYY-MM-DD")
        : null;

      let filtersToSend = columnFilters.map((f) => {
        if (f.id === "profit") {
          return [{ id: "total_profit", value: f.value }];
        }
        return f;
      });
      filtersToSend = filtersToSend.flat();

      const payload = {
        pagination: JSON.stringify({
          pageIndex: 0,
          pageSize: 10000000,
        }),
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: filtersToSend.length > 0 ? JSON.stringify(filtersToSend) : null,
        globalSearch: globalFilter || null,
        store_id: store || barStoreId,
        ...barParams(),
        category: dashCategory || undefined,
        all_statuses: dashCategory ? 1 : undefined,
        ...(viewMode === "revenue" && {
          period: revenuePeriod,
          salesPersonId: salesExecId,
        }),
      };

      const api =
        viewMode === "revenue" ? GetSalesPerformanceReportAPI : GetSalesReportAPI;
      const response = await apiData("get", api, payload);

      if (!response?.data?.result || response.data?.result.length === 0) {
        alert("No data to export!");
        return;
      }

      // Money and the profit percentage are written as real numbers (two
      // decimals via the cell format below), Profit split into AED and %.
      const r2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

      const exportData = response.data.result.map((item, index) =>
        viewMode === "revenue"
          ? {
              "#": index + 1,
              ...(revenuePeriod !== "all" && {
                Period: item.period_display || item.period_key || "",
              }),
              "Sales Executive": item.sales_person || "",
              "Total Invoices": item.total_orders || 0,
              "Total Quantity Sold": Number(item.total_quantity_sold) || 0,
              "Total Complimentary": item.total_complimentary || 0,
              "Total Sales Revenue (AED)": r2(item.total_sales),
              "Profit (AED)": r2(item.total_profit),
              "Profit %": r2(item.profit_percentage),
            }
          : {
              "#": index + 1,
              "Product Name": item.product_name || "",
              "SKU Code": item.sku || "",
              "Total Invoices": item.total_orders || 0,
              "Total Quantity Sold": Number(item.total_quantity_sold) || 0,
              "Total Sales Cost (AED)": r2(item.total_sales),
              "Profit (AED)": r2(item.total_profit),
              "Profit %": r2(item.profit_percentage),
            }
      );

      // Total footer — use the SAME backend `summary` the on-screen footer
      // renders, so the Excel footer total always matches the list footer.
      // (Previously it re-summed the exported rows, which drifted from the
      // summary aggregate and showed a different Total than the screen.)
      {
        const s = summary || {};
        const totalPct = parseFloat(s.profit_percentage || 0);
        exportData.push(
          viewMode === "revenue"
            ? {
                "Sales Executive": "Total",
                "Total Invoices": Number(s.total_invoices) || 0,
                "Total Quantity Sold": Number(s.total_quantity_sold) || 0,
                "Total Complimentary": Number(s.total_complimentary) || 0,
                "Total Sales Revenue (AED)": r2(s.total_sales),
                "Profit (AED)": r2(s.total_profit),
                "Profit %": r2(totalPct),
              }
            : {
                "Product Name": "Total",
                // Distinct invoice count (not the sum of per-product counts),
                // matching the on-screen footer and the Sales Performance total.
                "Total Invoices": Number(s.total_invoices) || 0,
                "Total Quantity Sold": Number(s.total_quantity_sold) || 0,
                "Total Sales Cost (AED)": r2(s.total_sales),
                "Profit (AED)": r2(s.total_profit),
                "Profit %": r2(totalPct),
              }
        );
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      // Two decimals on every money / percent column (found by its header).
      const headers = Object.keys(exportData[0]);
      Object.keys(ws).forEach((addr) => {
        const m = /^([A-Z]+)(\d+)$/.exec(addr);
        if (!m || m[2] === "1" || ws[addr].t !== "n") return;
        const colIndex = XLSX.utils.decode_col(m[1]);
        const h = headers[colIndex] || "";
        if (/\(AED\)|Profit %/.test(h)) ws[addr].z = "#,##0.00";
      });
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "SalesReport");

      const selectedSalesExec =
        salesExecId === "all"
          ? "All_Sales_Executives"
          : salesExecList.find((item) => String(item.id) === String(salesExecId))
              ?.employee_name || salesExecId;
      const fileName = buildExportFileName("SalesReport", [
        { label: "View", value: viewMode === "revenue" ? "Sales_Performance" : "Product_Wise" },
        viewMode === "revenue" ? { label: "Period", value: revenuePeriod } : null,
        viewMode === "revenue" ? { label: "Sales_Executive", value: selectedSalesExec } : null,
        store ? { label: "Store", value: store } : { label: "Store", value: "All_Stores" },
        selectedFromDate
          ? { label: "From", value: dayjs(selectedFromDate).format("DD-MM-YYYY") }
          : null,
        selectedToDate
          ? { label: "To", value: dayjs(selectedToDate).format("DD-MM-YYYY") }
          : null,
      ]);
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  async function fetchData(fd, td) {
    try {
      setLoading(true);

      fd = fd !== undefined ? fd : selectedFromDate;
      td = td !== undefined ? td : selectedToDate;

      const formattedFromDate = fd ? dayjs(fd).format("YYYY-MM-DD") : null;
      const formattedToDate = td ? dayjs(td).format("YYYY-MM-DD") : null;

      let filtersToSend = columnFilters.map((f) => {
        if (f.id === "profit") {
          return [{ id: "total_profit", value: f.value }];
        }
        return f;
      });

      filtersToSend = filtersToSend.flat();

      const payload = {
        pagination: JSON.stringify({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: filtersToSend.length > 0 ? JSON.stringify(filtersToSend) : null,
        globalSearch: globalFilter || null,
        store_id: store || barStoreId,
        ...barParams(),
        category: dashCategory || undefined,
        all_statuses: dashCategory ? 1 : undefined,
        ...(viewMode === "revenue" && {
          period: revenuePeriod,
          salesPersonId: salesExecId,
        }),
      };

      const api =
        viewMode === "revenue" ? GetSalesPerformanceReportAPI : GetSalesReportAPI;
      const response = await apiData("get", api, payload);
      if (response.success && response.data?.result) {
        setRowCount(response.data?.totalCount || 0);
        setTblList(response.data?.result || []);
        setSummary(response.data?.summary || {});
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  }

  const handleSearch = () => {
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    fetchData(fromDate, toDate);
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    fetchData(null, null);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    globalFilter,
    columnFilters,
    sorting,
    pagination,
    store,
    viewMode,
    revenuePeriod,
    salesExecId,
    dashCategory,
    reportBar,
  ]);

  const Columns = useMemo(() => {
    return [
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 50,
        Cell: ({ row }) => (
          <Tooltip title="Click to view product details" arrow>
            <Typography
              color="primary"
              sx={{ cursor: "pointer" }}
              onClick={() =>
                navigate(`/reports/sales-report/${row.original.product_id}`)
              }
            >
              {row.original.product_name}
            </Typography>
          </Tooltip>
        ),
        Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
      },
      {
        accessorKey: "sku",
        header: "SKU Code",
        size: 10,
      },
      {
        accessorKey: "total_orders",
        header: "Total Invoices",
        size: 80,
        Cell: ({ row }) => formatNumber(row.original.total_orders),
        // Footer uses the distinct invoice count (summary.total_invoices), not
        // the sum of the per-product counts above — an order spanning several
        // products is one invoice. This matches the Sales Performance total.
        Footer: () =>
          summary?.total_invoices != null ? (
            <Typography fontWeight={600}>
              {formatNumber(summary.total_invoices)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "total_quantity_sold",
        header: "Total Quantity Sold",
        size: 80,

        Cell: ({ row }) => {
          const total = Number(row.original.total_quantity_sold) || 0;
          return (
            <Tooltip
              title={total > 0 ? "View sales executives who sold this product" : "Nothing sold"}
              arrow
            >
              <Typography
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (total > 0) openSalesPersonBreakdown(row);
                }}
                sx={{
                  color: total > 0 ? "primary.main" : "inherit",
                  fontWeight: total > 0 ? 700 : 400,
                  textDecoration: total > 0 ? "underline" : "none",
                  cursor: total > 0 ? "pointer" : "default",
                }}
              >
                {formatNumber(total)} {row.original.uom}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_quantity_sold ? (
            <Typography fontWeight={600}>
              {formatNumber(summary.total_quantity_sold)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "total_sales",
        header: "Total Sales Cost",
        size: 100,
        Cell: ({ row }) =>
          row.original.total_sales
            ? `${formatAED(row.original.total_sales)}`
            : "N/A",
        Footer: () =>
          summary?.total_sales ? (
            <Typography fontWeight={600}>
              {formatAED(summary.total_sales)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "profit",
        id: "profit",
        header: "Profit",
        size: 100,
        Cell: ({ row }) => {
          const profit = parseFloat(row.original.total_profit || 0);
          const percentage = parseFloat(row.original.profit_percentage || 0);
          const isProfit = percentage >= 0;

          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {formatAED(profit)}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  variant="body2"
                  color={isProfit ? "success.main" : "error.main"}
                >
                  {percentage.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          );
        },
        Footer: () => {
          if (!summary) return "0 (0%)";
          const totalProfit = formatAED(summary.total_profit);
          const profitPercent = parseFloat(summary.profit_percentage || 0);
          const isProfit = profitPercent >= 0;

          return (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              fontWeight={600}
            >
              <Typography>{totalProfit}</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  sx={{ color: isProfit ? "success.main" : "error.main" }}
                >
                  ({profitPercent.toFixed(2)}%)
                </Typography>
              </Box>
            </Box>
          );
        },
      },
    ];
  }, [summary, navigate]);

  const goToComplimentaryInvoices = (row) => {
    const filters = [
      { id: "status", value: "Completed" },
      { id: "foc_status", value: "yes_partial" },
    ];

    if (row?.original?.user_id) {
      filters.push({ id: "created_by", value: row.original.user_id });
    }

    if (revenuePeriod === "day") {
      filters.push(
        { id: "order_date_from", value: row.original.period_key },
        { id: "order_date_to", value: row.original.period_key }
      );
    } else if (revenuePeriod === "month") {
      const start = dayjs(row.original.period_key, "YYYY-MM");
      filters.push(
        { id: "order_date_from", value: start.startOf("month").format("YYYY-MM-DD") },
        { id: "order_date_to", value: start.endOf("month").format("YYYY-MM-DD") }
      );
    } else if (revenuePeriod === "year") {
      const start = dayjs(row.original.period_key, "YYYY");
      filters.push(
        { id: "order_date_from", value: start.startOf("year").format("YYYY-MM-DD") },
        { id: "order_date_to", value: start.endOf("year").format("YYYY-MM-DD") }
      );
    } else {
      if (selectedFromDate) {
        filters.push({
          id: "order_date_from",
          value: dayjs(selectedFromDate).format("YYYY-MM-DD"),
        });
      }
      if (selectedToDate) {
        filters.push({
          id: "order_date_to",
          value: dayjs(selectedToDate).format("YYYY-MM-DD"),
        });
      }
    }

    navigate("/invoice/list", {
      state: {
        filters,
        reportTitle: "Complimentary Invoices",
      },
    });
  };

  const RevenueColumns = useMemo(() => {
    const periodHeader =
      revenuePeriod === "month"
        ? "Month"
        : revenuePeriod === "year"
          ? "Year"
          : "Date";
    // "all" (Overview) rolls every date into one row per person, so there's
    // no period to show — drop the column entirely instead of printing "All"
    // in every row. The TOTAL label moves onto whichever column is first.
    const showPeriodColumn = revenuePeriod !== "all";

    return [
      ...(showPeriodColumn
        ? [
            {
              accessorKey: "period_display",
              header: periodHeader,
              size: 70,
              Cell: ({ row }) => (
                <Typography fontWeight={600}>
                  {row.original.period_display || row.original.period_key}
                </Typography>
              ),
              Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
            },
          ]
        : []),
      {
        accessorKey: "sales_person",
        header: "Sales Executive",
        size: 90,
        Footer: () =>
          !showPeriodColumn ? (
            <Typography fontWeight={600}>TOTAL</Typography>
          ) : null,
      },
      {
        accessorKey: "total_orders",
        header: "Total Invoices",
        size: 80,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const total = Number(row.original.total_orders) || 0;
          return (
            <Tooltip
              title={total > 0 ? "View invoices for this sales executive" : "No invoices"}
              arrow
            >
              <Typography
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (total > 0) goToInvoicesForRow(row);
                }}
                sx={{
                  color: total > 0 ? "primary.main" : "inherit",
                  fontWeight: total > 0 ? 700 : 400,
                  textDecoration: total > 0 ? "underline" : "none",
                  cursor: total > 0 ? "pointer" : "default",
                }}
              >
                {formatNumber(total)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_invoices != null ? (
            <Typography fontWeight={600}>{formatNumber(summary.total_invoices)}</Typography>
          ) : null,
      },
      {
        accessorKey: "total_quantity_sold",
        header: "Total Quantity Sold",
        size: 80,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const total = Number(row.original.total_quantity_sold) || 0;
          return (
            <Tooltip
              title={total > 0 ? "View products sold by this sales executive" : "Nothing sold"}
              arrow
            >
              <Typography
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (total > 0) openProductBreakdown(row);
                }}
                sx={{
                  color: total > 0 ? "primary.main" : "inherit",
                  fontWeight: total > 0 ? 700 : 400,
                  textDecoration: total > 0 ? "underline" : "none",
                  cursor: total > 0 ? "pointer" : "default",
                }}
              >
                {formatNumber(total)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_quantity_sold ? (
            <Typography fontWeight={600}>
              {formatNumber(summary.total_quantity_sold)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "total_complimentary",
        header: "Total Complimentary",
        size: 90,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const total = Number(row.original.total_complimentary) || 0;
          return (
            <Tooltip
              title={total > 0 ? "View complimentary invoices" : "No complimentary invoices"}
              arrow
            >
              <Typography
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (total > 0) goToComplimentaryInvoices(row);
                }}
                sx={{
                  color: total > 0 ? "primary.main" : "inherit",
                  fontWeight: total > 0 ? 700 : 400,
                  textDecoration: total > 0 ? "underline" : "none",
                  cursor: total > 0 ? "pointer" : "default",
                }}
              >
                {formatNumber(total)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Tooltip title="View all complimentary invoices" arrow>
            <Typography
              component="span"
              fontWeight={600}
              onClick={(event) => {
                event.stopPropagation();
                if ((Number(summary?.total_complimentary) || 0) > 0) {
                  goToComplimentaryInvoices({ original: {} });
                }
              }}
              sx={{
                color: (Number(summary?.total_complimentary) || 0) > 0 ? "primary.main" : "inherit",
                textDecoration: (Number(summary?.total_complimentary) || 0) > 0 ? "underline" : "none",
                cursor: (Number(summary?.total_complimentary) || 0) > 0 ? "pointer" : "default",
              }}
            >
              {formatNumber(summary?.total_complimentary || 0)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: "total_sales",
        header: "Total Sales Revenue",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.total_sales
            ? `${formatAED(row.original.total_sales)}`
            : "N/A",
        Footer: () =>
          summary?.total_sales ? (
            <Typography fontWeight={600}>
              {formatAED(summary.total_sales)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "profit",
        id: "profit",
        header: "Profit",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const profit = parseFloat(row.original.total_profit || 0);
          const percentage = parseFloat(row.original.profit_percentage || 0);
          const isProfit = percentage >= 0;

          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {formatAED(profit)}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  variant="body2"
                  color={isProfit ? "success.main" : "error.main"}
                >
                  {percentage.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          );
        },
        Footer: () => {
          if (!summary) return "0 (0%)";
          const totalProfit = formatAED(summary.total_profit);
          const profitPercent = parseFloat(summary.profit_percentage || 0);
          const isProfit = profitPercent >= 0;

          return (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              fontWeight={600}
            >
              <Typography>{totalProfit}</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  sx={{ color: isProfit ? "success.main" : "error.main" }}
                >
                  ({profitPercent.toFixed(2)}%)
                </Typography>
              </Box>
            </Box>
          );
        },
      },
    ];
  }, [summary, revenuePeriod, selectedFromDate, selectedToDate, navigate]);

  // Shared by both drill-downs below (Invoice list + product breakdown):
  // when the row represents a specific day/month/year (not the "All"
  // overview), scope to that exact date range so the numbers shown match
  // exactly what's behind that row; for "All", fall back to whatever
  // From/To Date range is already narrowing the report.
  const computeRowDateRange = (row) => {
    if (revenuePeriod === "day") {
      return { fromDate: row.original.period_key, toDate: row.original.period_key };
    }
    if (revenuePeriod === "month") {
      const start = dayjs(row.original.period_key, "YYYY-MM");
      return {
        fromDate: start.startOf("month").format("YYYY-MM-DD"),
        toDate: start.endOf("month").format("YYYY-MM-DD"),
      };
    }
    if (revenuePeriod === "year") {
      const start = dayjs(row.original.period_key, "YYYY");
      return {
        fromDate: start.startOf("year").format("YYYY-MM-DD"),
        toDate: start.endOf("year").format("YYYY-MM-DD"),
      };
    }
    if (selectedFromDate || selectedToDate) {
      return {
        fromDate: selectedFromDate ? dayjs(selectedFromDate).format("YYYY-MM-DD") : undefined,
        toDate: selectedToDate ? dayjs(selectedToDate).format("YYYY-MM-DD") : undefined,
      };
    }
    return {};
  };

  // Sales Performance rows drill into the Invoice list, pre-filtered to that
  // row's sales executive and date range. Reuses the same location.state
  // contract OrdersList already reads via parseInitialFilters (see
  // salesPersonId / fromDate / toDate there).
  const goToInvoicesForRow = (row) => {
    const userId = row.original.user_id;
    if (!userId) return;

    const dateRange = computeRowDateRange(row);

    // Built as an explicit `filters` array (OrdersList's parseInitialFilters
    // uses this verbatim, bypassing its individual-key derivation) so the
    // Completed-only status scope can ride along too — Sales Performance's
    // own counts are all `WHERE order_status = 'Completed'` (see
    // getSalesPerformanceReport), so without this the invoice list would
    // also include that person's Cancelled/Returned/Pending orders and the
    // row count would never match "Total Orders Placed".
    const filters = [
      { id: "created_by", value: userId },
      { id: "status", value: "Completed" },
    ];
    if (dateRange.fromDate) filters.push({ id: "order_date_from", value: dateRange.fromDate });
    if (dateRange.toDate) filters.push({ id: "order_date_to", value: dateRange.toDate });

    navigate("/invoice/list", {
      state: {
        filters,
        reportTitle: `Invoices — ${row.original.sales_person || "Sales Executive"}`,
      },
    });
  };

  // "Total Quantity Sold" drills into a per-product breakdown for that same
  // sales executive + date range, shown in a dialog rather than navigating
  // away — there's no existing "product sales by salesperson" list page to
  // route to, and a compact dialog is enough to answer "what did they sell".
  const openProductBreakdown = async (row) => {
    const userId = row.original.user_id;
    if (!userId) return;

    const dateRange = computeRowDateRange(row);
    const salesPerson = row.original.sales_person || "Sales Executive";

    setProductBreakdown({ loading: true, salesPerson, rows: [], summary: {} });
    try {
      const result = await apiData("get", GetSalesPersonProductBreakdownAPI, {
        salesPersonId: userId,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      });
      setProductBreakdown({
        loading: false,
        salesPerson,
        rows: result.success ? result.data?.result || [] : [],
        summary: result.success ? result.data?.summary || {} : {},
      });
      if (!result.success) {
        childRef.current?.triggerAlert(
          JSON.stringify({
            message: result.message || "Failed to load product breakdown.",
            messageType: "error",
          })
        );
      }
    } catch (error) {
      console.error("Failed to fetch product breakdown:", error);
      setProductBreakdown({ loading: false, salesPerson, rows: [], summary: {} });
      childRef.current?.triggerAlert(
        JSON.stringify({ message: "Failed to load product breakdown.", messageType: "error" })
      );
    }
  };

  // Reverse of the above, from the Product-wise report's Total Quantity Sold
  // column: "which sales executives sold this product". Scoped to whatever
  // From/To Date range is currently applied to that report.
  const openSalesPersonBreakdown = async (row) => {
    const productId = row.original.product_id;
    if (!productId) return;

    const productName = row.original.product_name || "Product";
    const fromDateVal = selectedFromDate ? dayjs(selectedFromDate).format("YYYY-MM-DD") : undefined;
    const toDateVal = selectedToDate ? dayjs(selectedToDate).format("YYYY-MM-DD") : undefined;

    setSalesPersonBreakdown({ loading: true, productName, rows: [], summary: {} });
    try {
      const result = await apiData("get", GetProductSalesPersonBreakdownAPI, {
        productId,
        fromDate: fromDateVal,
        toDate: toDateVal,
      });
      setSalesPersonBreakdown({
        loading: false,
        productName,
        rows: result.success ? result.data?.result || [] : [],
        summary: result.success ? result.data?.summary || {} : {},
      });
      if (!result.success) {
        childRef.current?.triggerAlert(
          JSON.stringify({
            message: result.message || "Failed to load sales executive breakdown.",
            messageType: "error",
          })
        );
      }
    } catch (error) {
      console.error("Failed to fetch sales executive breakdown:", error);
      setSalesPersonBreakdown({ loading: false, productName, rows: [], summary: {} });
      childRef.current?.triggerAlert(
        JSON.stringify({
          message: "Failed to load sales executive breakdown.",
          messageType: "error",
        })
      );
    }
  };

  return (
    <>
      <Layout>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "#28a745",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <AlertMessage ref={childRef} />

        <Box className="employee-form bold-header">
          {/* Header Top Row */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography className="page_title" textAlign="left">
                {location.state?.reportTitle ||
                  location.state?.title ||
                  "Sales Report"}
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Typography color="text.primary">Sales-Report</Typography>
              </Breadcrumbs>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </Button>
          </Box>

          {dashCategory && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setDashCategory("");
                    handleClear();
                  }}
                >
                  Clear filter
                </Button>
              }
            >
              Filtered from the dashboard — category: {dashCategory}
              {selectedFromDate || selectedToDate
                ? ` (${selectedFromDate ? dayjs(selectedFromDate).format("DD-MM-YYYY") : "start"} to ${selectedToDate ? dayjs(selectedToDate).format("DD-MM-YYYY") : "today"})`
                : ""}
              . Includes all non-cancelled invoices, as the dashboard chart does.
            </Alert>
          )}

          {/* Row 1: Primary report view — which numbers to look at */}
          <Box sx={{ mt: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              color="primary"
              sx={reportViewToggleGroupSx}
              onChange={(e, v) => {
                if (!v) return;
                setViewMode(v);
                setColumnFilters([]);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <ToggleButton value="product" sx={reportViewToggleButtonSx}>
                Product-wise
              </ToggleButton>
              <ToggleButton value="revenue" sx={reportViewToggleButtonSx}>
                Sales Performance
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Row 2: Grouping, nested under Sales Performance — one size down
              and closer to Row 1 so it reads as "Sales Performance, broken
              down by ___" rather than a second, equally-weighted tab row.
              "All" (default) = totals per person, no date split; this is what
              the old standalone User-wise tab showed. */}
          {viewMode === "revenue" && (
            <Box sx={{ mt: 1, ml: 0.5 }}>
              <ToggleButtonGroup
                value={revenuePeriod}
                exclusive
                size="small"
                color="primary"
                sx={reportViewSubToggleGroupSx}
                onChange={(e, v) => {
                  if (!v) return;
                  setRevenuePeriod(v);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
              >
                <ToggleButton value="all" sx={reportViewSubToggleButtonSx}>
                  All
                </ToggleButton>
                <ToggleButton value="day" sx={reportViewSubToggleButtonSx}>
                  Per Day
                </ToggleButton>
                <ToggleButton value="month" sx={reportViewSubToggleButtonSx}>
                  Per Month
                </ToggleButton>
                <ToggleButton value="year" sx={reportViewSubToggleButtonSx}>
                  Per Year
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Row 3: Filter Bar — grouped in its own toolbar card so it reads
              as a distinct step from the view/grouping toggles above it. */}
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#fbfcfe",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {viewMode === "revenue" && (
                <Grid item xs={12} sm={6} md={3.5} lg={3.5}>
                  <Autocomplete
                    size="small"
                    options={[
                      { id: "all", employee_name: "All Sales Executives" },
                      ...salesExecList,
                    ]}
                    getOptionLabel={(option) => option.employee_name || ""}
                    value={
                      [
                        { id: "all", employee_name: "All Sales Executives" },
                        ...salesExecList,
                      ].find((opt) => String(opt.id) === String(salesExecId)) || {
                        id: "all",
                        employee_name: "All Sales Executives",
                      }
                    }
                    onChange={(event, newValue) => {
                      setSalesExecId(newValue ? newValue.id : "all");
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sales Executive"
                        size="small"
                        fullWidth
                        sx={{ "& .MuiInputBase-root": { height: 40 } }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={6}
                md={viewMode === "revenue" ? 3 : 4}
                lg={viewMode === "revenue" ? 3 : 4}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="From Date"
                    value={fromDate}
                    inputFormat="DD-MM-YYYY"
                    onChange={(newValue) => setFromDate(newValue)}
                    renderInput={(params) => (
                      <Tooltip title="Select order date to filter" arrow>
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{ "& .MuiInputBase-root": { height: 40 } }}
                        />
                      </Tooltip>
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={viewMode === "revenue" ? 3 : 4}
                lg={viewMode === "revenue" ? 3 : 4}
              >
                <Tooltip title="Select ending order date to filter" arrow>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      label="To Date"
                      value={toDate}
                      inputFormat="DD-MM-YYYY"
                      onChange={(newValue) => setToDate(newValue)}
                      renderInput={(params) => (
                        <Tooltip title="Select order date to filter" arrow>
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            sx={{ "& .MuiInputBase-root": { height: 40 } }}
                          />
                        </Tooltip>
                      )}
                    />
                  </LocalizationProvider>
                </Tooltip>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                md={viewMode === "revenue" ? 2.5 : 4}
                lg={viewMode === "revenue" ? 2.5 : 4}
              >
                <Box display="flex" gap={1} alignItems="center" height="40px">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      height: "40px",
                      minWidth: "80px",
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setColumnFilters([]);
                      setFromDate(null);
                      setToDate(null);
                      setSalesExecId("all");
                      setRevenuePeriod("all");
                      handleClear();
                    }}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      height: "40px",
                      minWidth: "80px",
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid mt={2}>
            <MaterialReactTable
              key={`${viewMode}-${revenuePeriod}-${salesExecId}`}
              enableGlobalFilter={false}
              enableGlobalFiltering={false}
              columns={viewMode === "revenue" ? RevenueColumns : Columns}
              data={tblList}
              enableTableFooter
              muiTableFooterProps={{
                sx: {
                  position: "sticky",
                  bottom: 0,
                  zIndex: 100,
                  backgroundColor: "#fff",
                  boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
                },
              }}
              muiToolbarAlertBannerProps={{
                sx: {
                  "& .MuiButtonBase-root": {
                    color: "red",
                    fontWeight: "500",
                    width: "100px",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(255, 153, 153, 0.8) 30%, rgba(255, 187, 187, 0.9) 100%)",
                    },
                  },
                },
              }}
              getRowId={(row) => row.id}
              initialState={{ showColumnFilters: false }}
              enableRowNumbers
              rowNumberMode="original"
              enableFullScreenToggle={false}
              enableStickyHeader
              manualFiltering
              manualPagination
              manualSorting
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              rowCount={rowCount}
              muiLinearProgressProps={{
                color: "inherit",
                sx: { height: "4px" },
              }}
              state={{
                rowSelection: selectedRowIds,
                columnFilters,
                globalFilter,
                isLoading: loading,
                pagination,
                sorting,
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 240px)",
                  minHeight: 450,
                  overflowY: "auto",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: "8px",
                  border: "1px solid #ccc",
                  color: "#fff !important",
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  "& svg": {
                    color: "#fff !important",
                  },
                  "& .MuiTableSortLabel-root": {
                    color: "#fff !important",
                    "&.Mui-active": {
                      color: "#fff !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                      },
                    },
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                  "& .MuiInputBase-input": {
                    color: "#fff !important",
                    textFillColor: "#fff !important",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#fff !important",
                    opacity: 0.8,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  borderBottom: "1px solid #ccc",
                  cursor: "default",
                  backgroundColor:
                    row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                },
              })}
              muiTableHeadCellFilterTextFieldProps={{
                sx: {
                  "& input": {
                    color: "#fff !important",
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important",
                  },
                },
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      p: "0.5rem",
                      justifyContent: "space-between",
                      flexGrow: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title="Export data to excel sheet" arrow>
                        <Button
                          onClick={handleExportData}
                          style={{ background: "#1a73e829" }}
                          startIcon={<FileDownload />}
                        >
                          Export To Excel
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </>
              )}
            />
          </Grid>
        </Box>
      </Layout>

      {/* Product breakdown — "which products did this sales executive
          sell" drill-down from the Total Quantity Sold column. */}
      <Dialog
        open={Boolean(productBreakdown)}
        onClose={() => setProductBreakdown(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Products Sold — {productBreakdown?.salesPerson}
        </DialogTitle>
        <DialogContent dividers>
          {productBreakdown?.loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !productBreakdown?.rows?.length ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No products sold in this period.
            </Typography>
          ) : (
            <>
              {/* Legend — one entry per visual cue used below. Only "Top
                  Seller" and the complimentary highlight; a separate "high
                  complimentary" badge would just duplicate what the amber
                  highlight + bold count on that column already say. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2.5, mb: 1.5 }}>
                {productBreakdown.rows.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label="Top Selling"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "primary.main",
                        color: "#fff",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Highest quantity sold product
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: "#fff3e0",
                      border: "1px solid #ffcc80",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Includes complimentary (FOC) items
                  </Typography>
                </Box>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty Sold</TableCell>
                      <TableCell align="right">Complimentary</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productBreakdown.rows.map((r, idx) => {
                      const hasComplimentary = Number(r.complimentary_qty) > 0;
                      const isTopSeller = idx === 0 && productBreakdown.rows.length > 1;
                      return (
                        <TableRow
                          key={r.product_id}
                          // !important: the app-wide TableCellExpander helper
                          // auto-tags every MUI table with .lmggt-clamp for
                          // consistent row heights (see tableEnhancements.css)
                          // and that stylesheet's odd/even zebra-striping rule
                          // is 4 selectors deep — plain sx background-color
                          // (a single generated class) loses to it every time.
                          sx={
                            hasComplimentary
                              ? {
                                  backgroundColor: "#fff3e0 !important",
                                  "&:hover": { backgroundColor: "#ffe8cc !important" },
                                }
                              : undefined
                          }
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">{r.product_name || "—"}</Typography>
                              {isTopSeller && (
                                <Chip
                                  label="Top Selling"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: "primary.main",
                                    color: "#fff",
                                  }}
                                />
                              )}
                            </Box>
                            {r.sku && (
                              <Typography variant="caption" color="text.secondary">
                                SKU: {r.sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{formatNumber(r.quantity_sold)}</TableCell>
                          <TableCell
                            align="right"
                            sx={hasComplimentary ? { fontWeight: 700, color: "#e65100" } : undefined}
                          >
                            {hasComplimentary ? formatNumber(r.complimentary_qty) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {formatAED(r.total_sales)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                {productBreakdown.summary?.total_products > 1 && (
                  <TableBody>
                    <TableRow sx={{ "& td": { fontWeight: 700, borderTop: "2px solid rgba(0,0,0,0.1)" } }}>
                      <TableCell>
                        TOTAL — {productBreakdown.summary.total_products} product
                        {productBreakdown.summary.total_products === 1 ? "" : "s"}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(productBreakdown.summary.total_quantity_sold)}
                      </TableCell>
                      <TableCell align="right">
                        {productBreakdown.summary.total_complimentary ? formatNumber(productBreakdown.summary.total_complimentary) : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {formatAED(productBreakdown.summary.total_sales)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductBreakdown(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reverse drill-down — "which sales executives sold this product",
          from the Product-wise report's Total Quantity Sold column. */}
      <Dialog
        open={Boolean(salesPersonBreakdown)}
        onClose={() => setSalesPersonBreakdown(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sold By — {salesPersonBreakdown?.productName}
        </DialogTitle>
        <DialogContent dividers>
          {salesPersonBreakdown?.loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !salesPersonBreakdown?.rows?.length ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Not sold by anyone in this period.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2.5, mb: 1.5 }}>
                {salesPersonBreakdown.rows.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label="Top Seller"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "primary.main",
                        color: "#fff",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Highest quantity sold
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 0.5,
                      bgcolor: "#fff3e0",
                      border: "1px solid #ffcc80",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Includes complimentary (FOC) items
                  </Typography>
                </Box>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                      <TableCell>Sales Executive</TableCell>
                      <TableCell align="right">Qty Sold</TableCell>
                      <TableCell align="right">Complimentary</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesPersonBreakdown.rows.map((r, idx) => {
                      const hasComplimentary = Number(r.complimentary_qty) > 0;
                      const isTopSeller = idx === 0 && salesPersonBreakdown.rows.length > 1;
                      return (
                        <TableRow
                          key={r.user_id}
                          // !important — see the Products Sold dialog above
                          // for why (TableCellExpander's .lmggt-clamp zebra
                          // striping otherwise beats a plain sx background).
                          sx={
                            hasComplimentary
                              ? {
                                  backgroundColor: "#fff3e0 !important",
                                  "&:hover": { backgroundColor: "#ffe8cc !important" },
                                }
                              : undefined
                          }
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">{r.sales_person || "—"}</Typography>
                              {isTopSeller && (
                                <Chip
                                  label="Top Seller"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: "primary.main",
                                    color: "#fff",
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatNumber(r.quantity_sold)}</TableCell>
                          <TableCell
                            align="right"
                            sx={hasComplimentary ? { fontWeight: 700, color: "#e65100" } : undefined}
                          >
                            {hasComplimentary ? formatNumber(r.complimentary_qty) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {formatAED(r.total_sales)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  {salesPersonBreakdown.summary?.total_sales_people > 1 && (
                    <TableBody>
                      <TableRow sx={{ "& td": { fontWeight: 700, borderTop: "2px solid rgba(0,0,0,0.1)" } }}>
                        <TableCell>
                          TOTAL — {salesPersonBreakdown.summary.total_sales_people} executive
                          {salesPersonBreakdown.summary.total_sales_people === 1 ? "" : "s"}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(salesPersonBreakdown.summary.total_quantity_sold)}
                        </TableCell>
                        <TableCell align="right">
                          {salesPersonBreakdown.summary.total_complimentary ? formatNumber(salesPersonBreakdown.summary.total_complimentary) : "—"}
                        </TableCell>
                        <TableCell align="right">
                          {formatAED(salesPersonBreakdown.summary.total_sales)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalesPersonBreakdown(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalesReports;
